import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useQuery } from 'react-apollo';

import FormGroup from '../../../../../../components/global/FormGroup';
import PhoneInput from '../../../../../../components/global/PhoneInput';
import Alert from '../../../../../../components/global/Alert';
import Button from '../../../../../../components/global/Button';
import { getFormikTouchedErrors } from '../../../../../../util/formik';
import useDebounce from '../../../../../../hooks/useDebounce';
import { accountEmailExists } from '../../../../../../graphql/queries/account';
import Label from '../../../../../../components/global/Label';
import { isValidEmail } from '../../../../../../util/email';
import CustomControl from '../../../../../../components/global/CustomControl';
import { LOGIN_AUTH_MODAL } from '../../../../../../components/auth/LoginAuthModal';
import useOpenModal from '../../../../../../hooks/useOpenModal';
import useGtagManualTracking from '../../../../../../services/gtag/useGtagManualTracking';
import { EVENTS } from '../../../../../../services/gtag/constants';

const propTypes = {
  className: PropTypes.string,
  values: PropTypes.shape({
    first_name: PropTypes.string,
    last_name: PropTypes.string,
    phone_number: PropTypes.string,
  }).isRequired,
  errors: PropTypes.object.isRequired,
  touched: PropTypes.object.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  handleChange: PropTypes.func.isRequired,
  handleBlur: PropTypes.func.isRequired,
  loading: PropTypes.bool.isRequired,
  requireHumanInput: PropTypes.bool,
  blockLogin: PropTypes.bool,
  hasLoginModalSupport: PropTypes.bool,
};

const RegistrationForm = ({
  className,
  handleSubmit,
  values,
  errors,
  touched,
  loading,
  handleChange,
  handleBlur,
  requireHumanInput,
  blockLogin,
  hasLoginModalSupport,
}) => {
  const openModal = useOpenModal();
  const { onTrackEvent } = useGtagManualTracking();
  const classes = classNames({
    'view-register-form': true,
    'shutterbug-registration-form': true,
    [className]: !!className,
  });
  const errorList = Object.values(
    getFormikTouchedErrors(errors, touched) ?? {},
  );

  const [debouncedAccountEmail] = useDebounce(values.email_address, 500, {
    trailing: true,
  });
  const { data } = useQuery(accountEmailExists, {
    variables: {
      email_address: debouncedAccountEmail,
    },
    skip: !debouncedAccountEmail || !isValidEmail(values.email_address),
    fetchPolicy: 'cache-first',
  });

  useEffect(() => {
    handleChange({
      target: {
        name: 'email_address_exists',
        value: data?.accountEmailExists?.exists,
      },
    });
  }, [data?.accountEmailExists?.exists]);

  return (
    <form
      className={classes}
      onSubmit={e => {
        onTrackEvent({
          event: EVENTS.CLICK_CREATE,
          data: {
            page_title: 'Complete Consumer Reg',
          },
        });
        handleSubmit(e);
      }}
    >
      <div className="row">
        <div className="col-lg-6 col-12">
          <FormGroup
            id="first_name"
            name="first_name"
            label="First name"
            placeholder="John"
            required
            onChange={handleChange}
            onBlur={handleBlur}
            value={values.first_name}
            labelProps={{ required: false }}
          />
        </div>

        <div className="col-lg-6 col-12">
          <FormGroup
            id="last_name"
            name="last_name"
            label="Last name"
            placeholder="Smith"
            onChange={handleChange}
            onBlur={handleBlur}
            value={values.last_name}
            labelProps={{ required: false }}
          />
        </div>

        <div className="col-12 col-md-6">
          <Label id="phone_number">Mobile number</Label>
          <PhoneInput
            id="phone_number"
            value={values.phone_number || '+1'}
            onChange={phone => {
              handleChange({
                target: {
                  name: 'phone_number',
                  value: phone,
                },
              });
            }}
            className="mb-2"
          />
        </div>

        <div className="col-12 col-md-6">
          <FormGroup
            id="email_address"
            name="email_address"
            label="Email address"
            placeholder="your@email.com"
            onChange={handleChange}
            onBlur={handleBlur}
            value={values.email_address}
            wrapperClassName="mb-2"
          />
          {values.email_address_exists && (
            <Alert type="danger">
              Sorry, that email address already belongs to a Waldo account.
            </Alert>
          )}
        </div>

        {requireHumanInput && (
          <div className="col-12 mb-2">
            <CustomControl
              type="checkbox"
              label="I'm not a robot"
              checked={values.force_allow_action}
              onChange={() =>
                handleChange({
                  target: {
                    name: 'force_allow_action',
                    value: !values.force_allow_action,
                  },
                })
              }
            />
          </div>
        )}
      </div>

      {errorList?.length > 0 && (
        <Alert type="danger" className="mb-4">
          <ul className="mb-0">
            {errorList.map(error => (
              <li key={error}>{error}</li>
            ))}
          </ul>
        </Alert>
      )}

      {hasLoginModalSupport && (
        <div>
          <p className="text-sm">
            Already have an account?{' '}
            <Button
              btnStyle="link"
              btnSize="sm"
              onClick={() => openModal(LOGIN_AUTH_MODAL)}
            >
              Sign in here.
            </Button>
          </p>
        </div>
      )}
      <div className="text-center">
        <p className="text-sm">
          By continuing, you agree to the{' '}
          <a href="/terms" target="_blank" rel="nofollow">
            Terms of Use
          </a>{' '}
          &{' '}
          <a href="/terms" target="_blank" rel="nofollow">
            Privacy Policy
          </a>
        </p>
      </div>

      <div className="text-center">
        <Button
          type="submit"
          disabled={
            loading ||
            values.email_address_exists ||
            (values.phone_number === '+1' && !values.email_address) ||
            blockLogin ||
            (requireHumanInput && !values.force_allow_action)
          }
          loading={loading}
        >
          {loading ? 'Processing...' : 'Continue'}
        </Button>
      </div>
    </form>
  );
};

RegistrationForm.propTypes = propTypes;

export default RegistrationForm;
